import React, { useState, useEffect } from "react";
import { authFetch } from "../api";
import Header from "../components/Header";

// Bedroom sizes in display order. Module scope: it never changes, and rebuilding it each
// render would remake the table's keys.
const SQFT_BEDS = [["studio", "Studio"], ["1br", "1BR"], ["2br", "2BR"],
                   ["3br", "3BR"], ["4br", "4BR"], ["5br", "5BR"]];

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("mortgage");
  const [saveMessage, setSaveMessage] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingSave, setPendingSave] = useState(null);

  // Config states
  const [mortgageConfig, setMortgageConfig] = useState(null);
  const [rentalConfig, setRentalConfig] = useState(null);
  const [sellingCostsConfig, setSellingCostsConfig] = useState(null);
  const [closingCostsConfig, setClosingCostsConfig] = useState(null);

  // Sqft estimation rules. Not part of /configs: they live one document per neighbourhood on
  // neighborhoods.sqft_estimation_rules, so they load and save through their own endpoints.
  const [sqftHoods, setSqftHoods] = useState([]);
  const [sqftDefault, setSqftDefault] = useState(null);
  const [sqftHoodId, setSqftHoodId] = useState("");
  const [sqftDraft, setSqftDraft] = useState(null);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const response = await authFetch("/configs");
      const configs = response.configs;

      setMortgageConfig(configs.mortgage || {});
      setRentalConfig(configs.rental || {});
      setSellingCostsConfig(configs.selling_costs || {});
      setClosingCostsConfig(configs.closing_costs || {});

      const sq = await authFetch("/configs/sqft-rules/all");
      setSqftHoods(sq.neighborhoods || []);
      setSqftDefault(sq.default || null);
    } catch (error) {
      console.error("Error loading configs:", error);
    } finally {
      setLoading(false);
    }
  };

  const requestSaveConfig = (configKey, configData) => {
    setPendingSave({ configKey, configData });
    setShowConfirmDialog(true);
  };

  const confirmSave = async () => {
    if (!pendingSave) return;

    setShowConfirmDialog(false);
    setSaving(true);
    setSaveMessage("");

    try {
      await authFetch(`/configs/${pendingSave.configKey}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ config_data: pendingSave.configData })
      });

      setSaveMessage("Configuration saved successfully!");
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (error) {
      console.error("Error saving config:", error);
      setSaveMessage("Error saving configuration");
    } finally {
      setSaving(false);
      setPendingSave(null);
    }
  };

  const updateSqftCell = (bedKey, idx, raw) => {
    const n = parseInt(raw, 10);
    setSqftDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      next.rooms = next.rooms || {};
      next.rooms[bedKey] = next.rooms[bedKey] || { std: SQFT_BEDS.findIndex(([k]) => k === bedKey) + 2, sf: [0, 0, 0, 0, 0] };
      next.rooms[bedKey].sf[idx] = isNaN(n) ? 0 : n;
      // The flat key mirrors the standard-room figure. Three readers still take the flat
      // keys (the rent predictor, model training, the batch estimator) and fall back to
      // hardcoded defaults without raising if they drift, so it is kept in step here.
      if (idx === 0) next[bedKey] = isNaN(n) ? 0 : n;
      return next;
    });
  };

  const saveSqftRules = async () => {
    if (!sqftHoodId || !sqftDraft) return;
    setSaving(true);
    setSaveMessage("");
    try {
      await authFetch(`/configs/sqft-rules/${sqftHoodId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rules: sqftDraft })
      });
      setSqftHoods((prev) => prev.map((h) =>
        String(h.id) === String(sqftHoodId)
          ? { ...h, sqft_estimation_rules: JSON.parse(JSON.stringify(sqftDraft)) }
          : h));
      setSaveMessage("Saved — applies to future estimates only.");
      setTimeout(() => setSaveMessage(""), 4000);
    } catch (error) {
      console.error("Error saving sqft rules:", error);
      setSaveMessage("Error saving sqft rules");
    } finally {
      setSaving(false);
    }
  };

  const cancelSave = () => {
    setShowConfirmDialog(false);
    setPendingSave(null);
  };

  const handleMortgageChange = (path, value) => {
    const updated = JSON.parse(JSON.stringify(mortgageConfig));
    const keys = path.split(".");
    let current = updated;

    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = parseFloat(value) || 0;

    setMortgageConfig(updated);
  };

  const handleRentalChange = (key, value) => {
    setRentalConfig({ ...rentalConfig, [key]: parseFloat(value) || 0 });
  };

  const handleSellingCostsChange = (key, value) => {
    setSellingCostsConfig({ ...sellingCostsConfig, [key]: parseFloat(value) || 0 });
  };

  const handleClosingCostsChange = (path, value) => {
    const updated = JSON.parse(JSON.stringify(closingCostsConfig));
    const keys = path.split(".");
    let current = updated;

    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = parseFloat(value) || 0;

    setClosingCostsConfig(updated);
  };

  if (loading) {
    return (
      <>
        <Header showLogout={true} />
        <div style={{ padding: "40px", textAlign: "center" }}>
          <div>Loading configurations...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header showLogout={true} />
      <div style={{ padding: "40px", maxWidth: "1200px", margin: "0 auto" }}>
        <h1 style={{ marginBottom: "10px" }}>Global Assumptions</h1>
        <p style={{ color: "#666", marginBottom: "30px" }}>
          Configure default assumptions used across all property calculations
        </p>

        {/* Save Message */}
        {saveMessage && (
          <div style={{
            padding: "12px 20px",
            marginBottom: "20px",
            borderRadius: "6px",
            backgroundColor: saveMessage.includes("Error") ? "#ffebee" : "#e8f5e9",
            color: saveMessage.includes("Error") ? "#c62828" : "#2e7d32",
            border: `1px solid ${saveMessage.includes("Error") ? "#ef5350" : "#66bb6a"}`
          }}>
            {saveMessage}
          </div>
        )}

        {/* Confirmation Dialog */}
        {showConfirmDialog && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "8px",
              maxWidth: "500px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
                <span style={{ fontSize: "32px", color: "#ff9800" }}>⚠️</span>
                <h2 style={{ margin: 0, fontSize: "20px" }}>Confirm Global Change</h2>
              </div>
              <p style={{ marginBottom: "25px", lineHeight: "1.6", color: "#555" }}>
                This will update the global assumptions and affect <strong>all properties</strong> across the entire system.
                All future calculations will use these new values.
              </p>
              <p style={{ marginBottom: "25px", fontWeight: "600", color: "#d32f2f" }}>
                Are you sure you want to proceed?
              </p>
              <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                <button
                  onClick={cancelSave}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "15px",
                    fontWeight: "500",
                    cursor: "pointer"
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSave}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#d32f2f",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: "pointer"
                  }}
                >
                  Yes, Update Globally
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{ borderBottom: "2px solid #e0e0e0", marginBottom: "30px" }}>
          <div style={{ display: "flex", gap: "0", flexWrap: "wrap" }}>
            {["mortgage", "rental", "selling_costs", "closing_costs", "sqft"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: "12px 24px",
                  border: "none",
                  backgroundColor: "transparent",
                  borderBottom: activeTab === tab ? "3px solid #1976d2" : "3px solid transparent",
                  color: activeTab === tab ? "#1976d2" : "#666",
                  fontWeight: activeTab === tab ? "600" : "normal",
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}
              >
                {tab === "sqft" ? "Sqft" : tab.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
              </button>
            ))}
          </div>
        </div>

        {/* Mortgage Config */}
        {activeTab === "mortgage" && mortgageConfig && (
          <div>
            <h2 style={{ marginBottom: "20px" }}>Mortgage Rates</h2>

            {/* Interest Only (IO) */}
            <div style={{ marginBottom: "30px" }}>
              <h3 style={{ fontSize: "18px", marginBottom: "15px", color: "#333" }}>
                Interest Only (IO) ARM Rates
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: "15px", alignItems: "center" }}>
                <label>7/1 ARM:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input
                    type="number"
                    step="0.01"
                    value={mortgageConfig.rates?.io?.["7_1_arm"] || 0}
                    onChange={(e) => handleMortgageChange("rates.io.7_1_arm", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                  <span>%</span>
                </div>

                <label>10/1 ARM:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input
                    type="number"
                    step="0.01"
                    value={mortgageConfig.rates?.io?.["10_1_arm"] || 0}
                    onChange={(e) => handleMortgageChange("rates.io.10_1_arm", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                  <span>%</span>
                </div>
              </div>
            </div>

            {/* Principal & Interest (PI) */}
            <div style={{ marginBottom: "30px" }}>
              <h3 style={{ fontSize: "18px", marginBottom: "15px", color: "#333" }}>
                Principal & Interest (PI) Rates
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: "15px", alignItems: "center" }}>
                <label>30-Year Fixed:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input
                    type="number"
                    step="0.01"
                    value={mortgageConfig.rates?.pi?.["30_year_fixed"] || 0}
                    onChange={(e) => handleMortgageChange("rates.pi.30_year_fixed", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                  <span>%</span>
                </div>

                <label>7/1 ARM:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input
                    type="number"
                    step="0.01"
                    value={mortgageConfig.rates?.pi?.["7_1_arm"] || 0}
                    onChange={(e) => handleMortgageChange("rates.pi.7_1_arm", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                  <span>%</span>
                </div>

                <label>10/1 ARM:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input
                    type="number"
                    step="0.01"
                    value={mortgageConfig.rates?.pi?.["10_1_arm"] || 0}
                    onChange={(e) => handleMortgageChange("rates.pi.10_1_arm", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                  <span>%</span>
                </div>
              </div>
            </div>

            {/* Down Payment */}
            <div style={{ marginBottom: "30px" }}>
              <h3 style={{ fontSize: "18px", marginBottom: "15px", color: "#333" }}>
                Down Payment
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: "15px", alignItems: "center" }}>
                <label>Down Payment:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input
                    type="number"
                    step="0.1"
                    value={mortgageConfig.down_payment_percent || 0}
                    onChange={(e) => handleMortgageChange("down_payment_percent", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                  <span>%</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => requestSaveConfig("mortgage", mortgageConfig)}
              disabled={saving}
              style={{
                padding: "12px 24px",
                backgroundColor: "#1976d2",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.6 : 1
              }}
            >
              {saving ? "Saving..." : "Save Mortgage Config"}
            </button>
          </div>
        )}

        {/* Rental Config */}
        {activeTab === "rental" && rentalConfig && (
          <div>
            <h2 style={{ marginBottom: "20px" }}>Rental Assumptions</h2>

            <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: "15px", alignItems: "center", marginBottom: "30px" }}>
              <label>Net Rent Increase (Annual):</label>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="number"
                  step="0.1"
                  value={rentalConfig.net_rent_increase_percent || 0}
                  onChange={(e) => handleRentalChange("net_rent_increase_percent", e.target.value)}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    width: "120px"
                  }}
                />
                <span>%</span>
              </div>
            </div>

            <button
              onClick={() => requestSaveConfig("rental", rentalConfig)}
              disabled={saving}
              style={{
                padding: "12px 24px",
                backgroundColor: "#1976d2",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.6 : 1
              }}
            >
              {saving ? "Saving..." : "Save Rental Config"}
            </button>
          </div>
        )}

        {/* Selling Costs Config */}
        {activeTab === "selling_costs" && sellingCostsConfig && (
          <div>
            <h2 style={{ marginBottom: "20px" }}>Selling Costs</h2>

            <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: "15px", alignItems: "center", marginBottom: "30px" }}>
              <label>Cost of Selling:</label>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="number"
                  step="0.001"
                  value={sellingCostsConfig.cost_of_selling_percent || 0}
                  onChange={(e) => handleSellingCostsChange("cost_of_selling_percent", e.target.value)}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    width: "120px"
                  }}
                />
                <span>%</span>
              </div>
            </div>

            <button
              onClick={() => requestSaveConfig("selling_costs", sellingCostsConfig)}
              disabled={saving}
              style={{
                padding: "12px 24px",
                backgroundColor: "#1976d2",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.6 : 1
              }}
            >
              {saving ? "Saving..." : "Save Selling Costs Config"}
            </button>
          </div>
        )}

        {/* Closing Costs Config */}
        {activeTab === "closing_costs" && closingCostsConfig && (
          <div>
            <h2 style={{ marginBottom: "20px" }}>Closing Costs</h2>

            {/* Mortgage Recording Tax */}
            <div style={{ marginBottom: "30px" }}>
              <h3 style={{ fontSize: "18px", marginBottom: "15px", color: "#333" }}>
                Mortgage Recording Tax (Condo)
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "250px 1fr", gap: "15px", alignItems: "center" }}>
                <label>Threshold:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>$</span>
                  <input
                    type="number"
                    value={closingCostsConfig.mortgage_recording_tax?.condo?.threshold || 0}
                    onChange={(e) => handleClosingCostsChange("mortgage_recording_tax.condo.threshold", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "150px"
                    }}
                  />
                </div>

                <label>Rate Below Threshold:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input
                    type="number"
                    step="0.01"
                    value={closingCostsConfig.mortgage_recording_tax?.condo?.rate_below || 0}
                    onChange={(e) => handleClosingCostsChange("mortgage_recording_tax.condo.rate_below", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                  <span>%</span>
                </div>

                <label>Rate Above Threshold:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input
                    type="number"
                    step="0.01"
                    value={closingCostsConfig.mortgage_recording_tax?.condo?.rate_above || 0}
                    onChange={(e) => handleClosingCostsChange("mortgage_recording_tax.condo.rate_above", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                  <span>%</span>
                </div>
              </div>
            </div>

            {/* Title Insurance */}
            <div style={{ marginBottom: "30px" }}>
              <h3 style={{ fontSize: "18px", marginBottom: "15px", color: "#333" }}>
                Title Insurance
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "250px 1fr", gap: "15px", alignItems: "center" }}>
                <label>Condo Rate:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input
                    type="number"
                    step="0.01"
                    value={closingCostsConfig.title_insurance?.condo_rate || 0}
                    onChange={(e) => handleClosingCostsChange("title_insurance.condo_rate", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                  <span>%</span>
                </div>
              </div>
            </div>

            {/* Mortgage Title Insurance */}
            <div style={{ marginBottom: "30px" }}>
              <h3 style={{ fontSize: "18px", marginBottom: "15px", color: "#333" }}>
                Mortgage Title Insurance
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "250px 1fr", gap: "15px", alignItems: "center" }}>
                <label>Rate:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input
                    type="number"
                    step="0.001"
                    value={closingCostsConfig.mortgage_title_insurance?.rate || 0}
                    onChange={(e) => handleClosingCostsChange("mortgage_title_insurance.rate", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                  <span>%</span>
                </div>
              </div>
            </div>

            {/* Fixed Fees */}
            <div style={{ marginBottom: "30px" }}>
              <h3 style={{ fontSize: "18px", marginBottom: "15px", color: "#333" }}>
                Fixed Fees
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "250px 1fr", gap: "15px", alignItems: "center" }}>
                <label>Application Fee:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>$</span>
                  <input
                    type="number"
                    value={closingCostsConfig.fees?.application_fee || 0}
                    onChange={(e) => handleClosingCostsChange("fees.application_fee", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                </div>

                <label>Appraisal Fee:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>$</span>
                  <input
                    type="number"
                    value={closingCostsConfig.fees?.appraisal_fee || 0}
                    onChange={(e) => handleClosingCostsChange("fees.appraisal_fee", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                </div>

                <label>Credit Check:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>$</span>
                  <input
                    type="number"
                    value={closingCostsConfig.fees?.credit_check || 0}
                    onChange={(e) => handleClosingCostsChange("fees.credit_check", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                </div>

                <label>Bank Attorney:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>$</span>
                  <input
                    type="number"
                    value={closingCostsConfig.fees?.bank_attorney || 0}
                    onChange={(e) => handleClosingCostsChange("fees.bank_attorney", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                </div>

                <label>Recording Fee:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>$</span>
                  <input
                    type="number"
                    value={closingCostsConfig.fees?.recording_fee || 0}
                    onChange={(e) => handleClosingCostsChange("fees.recording_fee", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                </div>

                <label>Attorney:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>$</span>
                  <input
                    type="number"
                    value={closingCostsConfig.fees?.attorney || 0}
                    onChange={(e) => handleClosingCostsChange("fees.attorney", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                </div>

                <label>Working Capital:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>$</span>
                  <input
                    type="number"
                    value={closingCostsConfig.fees?.working_capital || 0}
                    onChange={(e) => handleClosingCostsChange("fees.working_capital", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                </div>

                <label>Closer Fee:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>$</span>
                  <input
                    type="number"
                    value={closingCostsConfig.fees?.closer_fee || 0}
                    onChange={(e) => handleClosingCostsChange("fees.closer_fee", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
  
                {/* Sponsor (new development) — separate keys from the resale fees above.
                    The same number today, but they move for different reasons. */}
                <div style={{ gridColumn: "1 / -1", marginTop: "18px", paddingTop: "14px",
                              borderTop: "1px solid #e0e0e0", fontWeight: "600", color: "#333" }}>
                  Sponsor (new development)
                </div>
                <label>Sponsor Attorney:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>$</span>
                  <input
                    type="number"
                    value={closingCostsConfig.fees?.sponsor_attorney || 0}
                    onChange={(e) => handleClosingCostsChange("fees.sponsor_attorney", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                </div>
                <label>Sponsor Closer Fee:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>$</span>
                  <input
                    type="number"
                    value={closingCostsConfig.fees?.sponsor_closer_fee || 0}
                    onChange={(e) => handleClosingCostsChange("fees.sponsor_closer_fee", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                </div>
                <label>Working Capital (months of HOA):</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>×</span>
                  <input
                    type="number"
                    value={closingCostsConfig.fees?.sponsor_working_capital_months || 0}
                    onChange={(e) => handleClosingCostsChange("fees.sponsor_working_capital_months", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                  <span style={{ color: "#666", fontSize: "13px" }}>multiplied by the monthly common charge</span>
                </div>
              </div>

                {/* Sponsor (new development) — separate keys from the resale fees above.
                    The same number today, but they move for different reasons. */}
                <div style={{ gridColumn: "1 / -1", marginTop: "18px", paddingTop: "14px",
                              borderTop: "1px solid #e0e0e0", fontWeight: "600", color: "#333" }}>
                  Sponsor (new development)
                </div>
                <label>Sponsor Attorney:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>$</span>
                  <input
                    type="number"
                    value={closingCostsConfig.fees?.sponsor_attorney || 0}
                    onChange={(e) => handleClosingCostsChange("fees.sponsor_attorney", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                </div>
                <label>Sponsor Closer Fee:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>$</span>
                  <input
                    type="number"
                    value={closingCostsConfig.fees?.sponsor_closer_fee || 0}
                    onChange={(e) => handleClosingCostsChange("fees.sponsor_closer_fee", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                </div>
                <label>Working Capital (months of HOA):</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>×</span>
                  <input
                    type="number"
                    value={closingCostsConfig.fees?.sponsor_working_capital_months || 0}
                    onChange={(e) => handleClosingCostsChange("fees.sponsor_working_capital_months", e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      width: "120px"
                    }}
                  />
                  <span style={{ color: "#666", fontSize: "13px" }}>multiplied by the monthly common charge</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => requestSaveConfig("closing_costs", closingCostsConfig)}
              disabled={saving}
              style={{
                padding: "12px 24px",
                backgroundColor: "#1976d2",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.6 : 1
              }}
            >
              {saving ? "Saving..." : "Save Closing Costs Config"}
            </button>
          </div>
        )}

        {/* Sqft estimation rules.
            Two things make this tab different from the four above: the values live per
            neighbourhood rather than globally, and a change here is FORWARD-ONLY -- listings
            already carrying an estimate keep it until the batch estimator runs again. */}
        {activeTab === "sqft" && (
          <div>
            <h2 style={{ marginBottom: "8px" }}>Square Footage Estimation</h2>
            <p style={{ color: "#666", fontSize: "14px", marginTop: 0, marginBottom: "24px" }}>
              Used when a listing has no reported square footage. A unit is sized from its
              bedroom count and its <strong>room count</strong> &mdash; a 4-room 2BR and a
              7-room 2BR are not the same size. &ldquo;Std&rdquo; is the room count treated as
              typical for that bedroom size; the columns to its right are that many rooms above it.
            </p>

            <div style={{ backgroundColor: "#fff8e1", border: "1px solid #ffe082",
                          borderRadius: "6px", padding: "12px 16px", marginBottom: "24px",
                          fontSize: "14px", color: "#5d4037" }}>
              Changes apply to <strong>future estimates only</strong>. Listings already holding
              an estimated figure keep it until the estimator next runs &mdash; nothing is
              recalculated when you save.
            </div>

            <h3 style={{ marginBottom: "8px" }}>App default</h3>
            <p style={{ color: "#666", fontSize: "13px", marginTop: 0 }}>
              What a neighbourhood falls back to when it has no rules of its own.
            </p>
            {sqftDefault && (
              <table style={{ borderCollapse: "collapse", marginBottom: "32px", fontSize: "14px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                    {["", "Std rooms", "Std", "+1", "+2", "+3", "+4 or more"].map((h) => (
                      <th key={h} style={{ padding: "8px 14px", textAlign: "left",
                                           borderBottom: "2px solid #e0e0e0" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SQFT_BEDS.map(([key, label]) => {
                    const row = (sqftDefault.rooms || {})[key];
                    if (!row) return null;
                    return (
                      <tr key={key}>
                        <td style={{ padding: "6px 14px", fontWeight: "600" }}>{label}</td>
                        <td style={{ padding: "6px 14px", color: "#666" }}>{row.std}</td>
                        {row.sf.map((v, i) => (
                          <td key={i} style={{ padding: "6px 14px" }}>{v.toLocaleString()}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            <h3 style={{ marginBottom: "12px" }}>Per neighbourhood</h3>
            <select
              value={sqftHoodId}
              onChange={(e) => {
                const id = e.target.value;
                setSqftHoodId(id);
                const h = sqftHoods.find((x) => String(x.id) === String(id));
                // Deep copy: editing the row in sqftHoods directly would leave the list
                // showing unsaved edits as though they had been stored.
                setSqftDraft(h ? JSON.parse(JSON.stringify(h.sqft_estimation_rules || {})) : null);
              }}
              style={{ padding: "8px 12px", fontSize: "15px", borderRadius: "6px",
                       border: "2px solid #ddd", minWidth: "280px", marginBottom: "20px" }}
            >
              <option value="">Choose a neighbourhood…</option>
              {sqftHoods.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.neighborhood_name} ({h.borough})
                </option>
              ))}
            </select>

            {sqftDraft && (
              <div>
                <table style={{ borderCollapse: "collapse", marginBottom: "20px", fontSize: "14px" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      {["", "Std rooms", "Std", "+1", "+2", "+3", "+4 or more"].map((h) => (
                        <th key={h} style={{ padding: "8px 14px", textAlign: "left",
                                             borderBottom: "2px solid #e0e0e0" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {SQFT_BEDS.map(([key, label]) => {
                      const row = (sqftDraft.rooms || {})[key] || { std: "", sf: [0, 0, 0, 0, 0] };
                      return (
                        <tr key={key}>
                          <td style={{ padding: "6px 14px", fontWeight: "600" }}>{label}</td>
                          <td style={{ padding: "6px 14px", color: "#666" }}>{row.std}</td>
                          {row.sf.map((v, i) => (
                            <td key={i} style={{ padding: "4px 6px" }}>
                              <input
                                type="number"
                                value={v}
                                onChange={(e) => updateSqftCell(key, i, e.target.value)}
                                style={{ width: "80px", padding: "6px 8px", fontSize: "14px",
                                         border: "2px solid #ddd", borderRadius: "4px" }}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p style={{ color: "#666", fontSize: "13px" }}>
                  The <strong>Std</strong> column is also the figure used when a listing has no
                  room count at all, so it is the one that matters most.
                </p>
                <button
                  onClick={saveSqftRules}
                  disabled={saving}
                  style={{ padding: "12px 28px", backgroundColor: "#1976d2", color: "#fff",
                           border: "none", borderRadius: "6px", fontSize: "15px",
                           fontWeight: "600", cursor: saving ? "not-allowed" : "pointer" }}
                >
                  {saving ? "Saving…" : "Save neighbourhood rules"}
                </button>
              </div>
            )}
          </div>
        )}

      </div>
    </>
  );
}
